import { HelpCircle, LogOut, Moon, Settings, Sun } from "lucide-react";
import * as React from "react";

import { authApi } from "@/api/auth";
import { CommandPaletteHint } from "@/components/CommandPaletteHint";
import { NotificationBell } from "@/components/NotificationBell";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/stores/auth";
import { useOnboardingStore } from "@/stores/onboarding";
import { useRouteStore } from "@/stores/route";
import { useUIStore } from "@/stores/ui";

/**
 * Slim TopBar (P2.1-S1 refactor).
 *
 * Trước: chứa logo + admin menu items trong avatar dropdown.
 * Sau: chỉ giữ theme toggle + NotificationBell + Avatar dropdown
 * (Settings + Logout). Logo + nav items giờ sống trong PrimarySidebar.
 */
export function TopBar(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const { theme, toggleTheme } = useUIStore();
  const navigate = useRouteStore((s) => s.navigate);
  const openHelp = useOnboardingStore((s) => s.openHelp);

  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const handleLogout = async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch {
      // ignore
    }
    clearAuth();
    navigate("chat");
  };

  return (
    <header className="h-14 shrink-0 bg-background flex items-center px-4 gap-2 border-b">
      {/* Command palette entry (left-aligned with logo). Logo lives in PrimarySidebar. */}
      <CommandPaletteHint />

      {/* Spacer pushes remaining controls right */}
      <div className="flex-1" />

      <Button
        variant="ghost"
        size="icon"
        onClick={openHelp}
        aria-label="Help & shortcuts"
        title="Help (Cmd+/)"
      >
        <HelpCircle className="size-4" />
      </Button>

      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        aria-label="Toggle theme"
      >
        {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
      </Button>

      <NotificationBell />

      {user ? (
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            className="flex items-center"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="User menu"
          >
            <Avatar
              size="sm"
              src={user.avatar_url}
              alt={user.display_name ?? user.email}
              fallback={(user.display_name ?? user.email).slice(0, 2).toUpperCase()}
            />
          </button>
          {menuOpen ? (
            <div className="absolute right-0 mt-2 w-56 rounded-md border bg-popover shadow-md z-50 py-1 text-sm">
              <div className="px-3 py-2 border-b">
                <div className="font-medium truncate">
                  {user.display_name ?? user.email}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {user.email}
                </div>
              </div>
              <button
                type="button"
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted text-left"
                onClick={() => {
                  setMenuOpen(false);
                  navigate("settings.personal");
                }}
              >
                <Settings className="size-4" /> Cài đặt
              </button>
              <button
                type="button"
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted text-left text-destructive border-t"
                onClick={() => {
                  setMenuOpen(false);
                  void handleLogout();
                }}
              >
                <LogOut className="size-4" /> Đăng xuất
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </header>
  );
}
